import React, { ReactElement, useEffect, useState } from 'react';
import { DashboardPage } from 'pages/DashboardPage';
import { FC_LICENSING_AUTH_ACCESS_TOKEN, FC_LICENSING_AUTH_TYPE_ACCOUNT } from 'requests/auth';
import { useLocation, useNavigate } from 'react-router-dom';
import { ERouteLinks } from 'models/route';
import { AccountTypePage } from 'pages/AccountTypePage';
import { LoginPage } from 'pages/LoginPage';
import { AccountType } from 'constants/AccountType';
import { TwoFactorAuthPage } from 'pages/TwoFactorAuthPage';
import { CreateAnAccount } from 'pages/CreateAnAccount';
import { SetPasswordPage } from 'pages/SetPasswordPage';
import { LoginType } from 'models/auth';
import { SessionStorageKeys } from 'constants/Storage';

export interface IData {
  email: string;
  password: string;
  type: AccountType | null;
  loginType: LoginType;
}

const AuthPage = (): ReactElement => {
  const [formStep, setFormStep] = useState(1);
  const [type, setType] = useState<AccountType>(AccountType.individual);
  const [data, setData] = useState<IData>({
    email: '',
    password: '',
    type: AccountType.individual,
    loginType: LoginType.Sms,
  });
  const navigate = useNavigate();
  const { state: routerState }: any = useLocation();

  useEffect(() => {
    if (localStorage.getItem(FC_LICENSING_AUTH_ACCESS_TOKEN)) {
      navigate(ERouteLinks.Dashboard);
    }
  }, [navigate]);

  useEffect(() => {
    if (routerState?.isRedirectToTwoFactorAuthPage) {
      setData((prevState: IData) => ({ ...prevState, email: routerState?.email }));
      setFormStep(3);
    } else if (routerState?.type || localStorage.getItem(FC_LICENSING_AUTH_TYPE_ACCOUNT)) {
      const prevType =
        routerState?.type || localStorage.getItem(FC_LICENSING_AUTH_TYPE_ACCOUNT);
      setType(prevType);
      setData((prevState: IData) => ({ ...prevState, type: prevType }));
      setFormStep(2);
    }
  }, []);

  useEffect(() => () => window.history.replaceState({}, document.title), []);

  const login = (t: AccountType) => {
    setType(t);
    window.sessionStorage.setItem(SessionStorageKeys.type, t);
    setFormStep(2);
  };

  const signIn = (t: AccountType) => {
    setType(t);
    window.sessionStorage.setItem(SessionStorageKeys.type, t);
    setFormStep(4);
  };

  const renderAuthPage = () => {
    switch (formStep) {
      case 1:
        return <AccountTypePage login={login} signIn={signIn} />;
      case 2:
        return (
          <LoginPage
            setData={setData}
            type={type}
            email={data.email}
            changePage={(step) => setFormStep(step)}
          />
        );
      case 3:
        return (
          <TwoFactorAuthPage
            type={type}
            email={data.email}
            password={data.password}
            loginType={data.loginType}
            changePage={(step) => setFormStep(step)}
          />
        );
      case 4:
        return (
          <CreateAnAccount
            type={type}
            setData={setData}
            changePage={(step) => setFormStep(step)}
          />
        );
      case 5:
        return <DashboardPage />;
      case 6:
        return (
          <SetPasswordPage
            oldPassword={data.password}
            type={type}
            setData={setData}
            changePage={(step) => setFormStep(step)}
          />
        );
      default:
        return <div />;
    }
  };

  return <div>{renderAuthPage()}</div>;
};
export { AuthPage };
